/**
 * Time-to-live (TTL) options for shares and API keys
 * Values in milliseconds
 */

export const MINUTE = 60 * 1000
export const HOUR = 60 * MINUTE
export const DAY = 24 * HOUR
export const YEAR = 365 * DAY

export const EXPIRY_OPTIONS = {
  '1h': 1 * HOUR,
  '12h': 12 * HOUR,
  '1d': 1 * DAY,
  '3d': 3 * DAY,
  '7d': 7 * DAY,
  '30d': 30 * DAY,
  '90d': 90 * DAY,
  '180d': 180 * DAY,
  '1y': 365 * DAY,
  never: null,
} as const

export type ExpiryOption = keyof typeof EXPIRY_OPTIONS

/** Default expiry when not specified */
export const DEFAULT_EXPIRY: ExpiryOption = '7d'

/** "Never expires" uses a far future date (100 years) */
export const NEVER_EXPIRES_MS = 100 * YEAR

/** Threshold to detect "never" expiration — any expiry more than 50 years from now is treated as "never" */
export const NEVER_EXPIRES_THRESHOLD_MS = 50 * YEAR

/** Check if an expiration timestamp represents "never expires" */
export function isNeverExpires(expiresAt: number, now = Date.now()): boolean {
  return expiresAt - now > NEVER_EXPIRES_THRESHOLD_MS
}

/**
 * Calculate expiration timestamp from an expiry option
 */
export function calculateExpiresAt(expiresIn: ExpiryOption, now = Date.now()): number {
  const expiryMs = EXPIRY_OPTIONS[expiresIn]
  if (expiryMs === null) {
    return now + NEVER_EXPIRES_MS
  }
  return now + expiryMs
}
